import axios from "axios";
import { useEffect } from "react";
import LoadingModal from "../../components/LoadingModal";
import { setImpersonationSession } from "../../utils/impersonation.utils";

/**
 * Entry point for admin "login as user" (GOD mode) sessions.
 *
 * The Django admin redirects here with the impersonation token in the URL
 * fragment (never the query string, so it stays out of server logs). An inline
 * script at the very top of public/index.html moves the fragment into
 * sessionStorage and strips it from the URL before any analytics script can
 * capture the full location; this page reads that handoff (with a direct-hash
 * fallback).
 *
 * The candidate token is VERIFIED against the backend before it is trusted:
 * only a token that /users/userinfo/ reports as a live ImpersonationSession is
 * accepted, and all banner metadata is derived from that verified response.
 * This prevents a crafted /impersonate link from planting an attacker's own
 * ordinary token (which would spoof the GOD MODE banner while quietly logging
 * the admin into the attacker's account). The session is stored tab-scoped in
 * sessionStorage so it never disturbs the admin's own login or other tabs.
 */
export default function Impersonate() {
  useEffect(() => {
    const run = async () => {
      let handoff = "";
      try {
        handoff = sessionStorage.getItem("impersonation_handoff") || "";
        sessionStorage.removeItem("impersonation_handoff");
      } catch {
        // sessionStorage unavailable — fall back to the hash below
      }
      if (!handoff && window.location.hash.length > 1) {
        handoff = window.location.hash.slice(1);
        window.history.replaceState(null, "", window.location.pathname);
      }

      const token = new URLSearchParams(handoff).get("token");
      if (!token) {
        window.location.replace("/login");
        return;
      }

      // Verify server-side that this really is a live impersonation session.
      let impersonation:
        | { active?: boolean; admin_email?: string; user_email?: string; expires_at?: string }
        | undefined;
      try {
        const resp = await axios.get(`${process.env.REACT_APP_SERVER_URL}/users/userinfo/`, {
          headers: { Authorization: `Token ${token}` },
          withCredentials: true,
        });
        impersonation = resp.data?.impersonation;
      } catch {
        // fall through to the reject path below
      }

      if (!impersonation?.active) {
        // Not a live impersonation token — refuse it (never store it).
        window.location.replace("/login");
        return;
      }

      // Store tab-scoped, with metadata derived entirely from server truth.
      try {
        setImpersonationSession(token, {
          email: impersonation.user_email || "",
          admin: impersonation.admin_email || "",
          expires: impersonation.expires_at || "",
          started_at: new Date().toISOString(),
        });
      } catch {
        // Storage blocked/full — can't hold the session; fail closed.
        window.location.replace("/login");
        return;
      }

      // Full reload so the app bootstraps cleanly with the new token.
      window.location.replace("/home");
    };
    void run();
  }, []);

  return <LoadingModal />;
}
